import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, takeUntil } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData, submitSearch } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { getStageColor, getStageBgColor, StageName } from '../../../../../../shared/utils/bracket.utils';
import { CompanyThemePopupComponent } from '../../general/company-theme-popup/company-theme-popup.component';
import { LoadingProgressComponent, LoadingStage } from '../../../../../../shared/components/loading-progress/loading-progress.component';

const SIDENAV_KEY = 'company_technology_summary';

const LOADING_STAGES: LoadingStage[] = [
  { text: 'Scanning the company’s patent footprint…', atSeconds: 0 },
  { text: 'Mapping technology clusters…', atSeconds: 10 },
  { text: 'Measuring exposure…', atSeconds: 22 },
];
const PAGE_SIZE = 10;
const STAGE_ORDER: StageName[] = ['Emerging', 'Accelerating', 'Maturing', 'Declining'];
const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MIN_LABEL_PCT = 6;

Chart.register(...registerables);

const inBarLabelsPlugin = {
  id: 'techSummaryInBarLabels',
  afterDatasetsDraw(chart: Chart) {
    const { ctx, scales } = chart;
    const xScale = scales['x'];
    if (!xScale) return;
    const minPx = xScale.getPixelForValue(MIN_LABEL_PCT) - xScale.getPixelForValue(0);

    ctx.save();
    ctx.font = `600 13px ${FONT_FAMILY}`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      const bar = meta.data[0] as any;
      if (!bar) return;
      const { x, y, width, height, base } = bar.getProps(['x', 'y', 'width', 'height', 'base'], true);
      const segWidth = Math.abs(x - base);
      if (segWidth < minPx) return;
      const value = dataset.data[0] as number;
      if (value == null) return;
      const cx = (x + base) / 2;
      const cy = y;
      ctx.fillText(`${value.toFixed(1)}%`, cx, cy);
    });

    ctx.restore();
  },
};

export interface ITechSummaryRow {
  cluster_id: number;
  cluster_name: string;
  stage_name: string;
  exposure: number;
}

interface StageStat {
  stage: StageName;
  pct: number;
  count: number;
  color: string;
  bg: string;
}

@Component({
  selector: 'app-company-technology-summary',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, BaseChartDirective, LoadingProgressComponent],
  templateUrl: './company-technology-summary.component.html',
  styleUrl: './company-technology-summary.component.css',
})
export class CompanyTechnologySummaryComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;

  private destroy$ = new Subject<void>();

  allRows: ITechSummaryRow[] = [];
  filteredRows: ITechSummaryRow[] = [];
  pagedRows: ITechSummaryRow[] = [];

  activeFilter: StageName | 'All' = 'All';
  stageStats: StageStat[] = [];

  currentPage = 0;
  pageSize = PAGE_SIZE;
  totalPages = 0;

  chartjsData!: ChartConfiguration<'bar'>['data'];
  chartjsOptions!: ChartConfiguration<'bar'>['options'];
  readonly chartPlugins = [inBarLabelsPlugin];
  readonly LOADING_STAGES = LOADING_STAGES;

  showSpinner = false;
  showChart = false;
  showTable = false;
  loadingFailed = false;

  constructor(
    private store: Store<AppState>,
    private resultsService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId'] && this.tabId) {
      this.reset();
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.allRows = [];
    this.filteredRows = [];
    this.pagedRows = [];
    this.stageStats = [];
    this.activeFilter = 'All';
    this.currentPage = 0;
    this.showChart = false;
    this.showTable = false;
    this.loadingFailed = false;
  }

  private loadData(): void {
    this.reset();
    this.showSpinner = true;
    const currentTabId = this.tabId;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, SIDENAV_KEY)).pipe(
      take(1),
      switchMap((cached: ITechSummaryRow[] | null) => {
        if (cached) {
          return of({ data: cached, targetTabId: currentTabId });
        }
        const entityid = this.entityids?.[0];
        if (!entityid) return of({ data: [] as ITechSummaryRow[], targetTabId: currentTabId });
        return this.resultsService.getCompanyTechnologySummary(entityid, currentTabId).pipe(
          switchMap((res) => {
            const payload = res.body?.payload;
            const data: ITechSummaryRow[] = payload?.data ?? [];
            const targetTabId: string = payload?.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: SIDENAV_KEY, data }));
            return of({ data, targetTabId });
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ data, targetTabId }) => {
        if (this.tabId !== currentTabId || targetTabId !== currentTabId) return;
        this.showSpinner = false;
        if (!data.length) {
          this.loadingFailed = true;
          return;
        }
        this.allRows = data;
        this.applyFilter(this.activeFilter);
        this.buildChart();
        this.showChart = true;
        this.showTable = true;
      },
      error: () => {
        if (this.tabId === currentTabId) {
          this.showSpinner = false;
          this.loadingFailed = true;
        }
      },
    });
  }

  applyFilter(filter: StageName | 'All'): void {
    this.activeFilter = filter;
    this.filteredRows = filter === 'All'
      ? [...this.allRows]
      : this.allRows.filter(r => r.stage_name === filter);
    this.currentPage = 0;
    this.totalPages = Math.ceil(this.filteredRows.length / this.pageSize);
    this.updatePage();
  }

  toggleStage(stage: StageName): void {
    this.applyFilter(this.activeFilter === stage ? 'All' : stage);
  }

  updatePage(): void {
    const start = this.currentPage * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.updatePage();
  }

  private buildChart(): void {
    const stageTotals: Record<string, number> = {};
    const stageCounts: Record<string, number> = {};
    for (const row of this.allRows) {
      stageTotals[row.stage_name] = (stageTotals[row.stage_name] || 0) + row.exposure;
      stageCounts[row.stage_name] = (stageCounts[row.stage_name] || 0) + 1;
    }

    this.stageStats = STAGE_ORDER.map(stage => ({
      stage,
      pct: stageTotals[stage] || 0,
      count: stageCounts[stage] || 0,
      color: getStageColor(stage),
      bg: getStageBgColor(stage),
    }));

    this.chartjsData = {
      labels: [''],
      datasets: STAGE_ORDER.map(stage => ({
        label: stage,
        data: [stageTotals[stage] || 0],
        backgroundColor: getStageColor(stage),
        borderRadius: 6,
        borderSkipped: false,
        barThickness: 36,
      })),
    };

    this.chartjsOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: 0 },
      animation: { duration: 600 },
      scales: {
        x: { display: false, min: 0, max: 100, stacked: true },
        y: { display: false, stacked: true },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#ffffff',
          titleColor: '#64748b',
          bodyColor: '#0f172a',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          borderWidth: 1,
          padding: 10,
          cornerRadius: 8,
          displayColors: true,
          titleFont: { family: FONT_FAMILY, size: 12, weight: 600 },
          bodyFont: { family: FONT_FAMILY, size: 13, weight: 600 },
          callbacks: {
            title: () => '',
            label: ctx => `${ctx.dataset.label}: ${(ctx.parsed.x as number).toFixed(1)}%`,
          },
        },
      },
    };
  }

  getStageColor(stage: string): string {
    return getStageColor(stage);
  }

  getStageBgColor(stage: string): string {
    return getStageBgColor(stage);
  }

  get pageNumbers(): number[] {
    const max = 10;
    if (this.totalPages <= max) {
      return Array.from({ length: this.totalPages }, (_, i) => i);
    }
    const start = Math.min(
      Math.max(0, this.currentPage - Math.floor(max / 2)),
      this.totalPages - max
    );
    return Array.from({ length: max }, (_, i) => start + i);
  }

  submitTheme(row: ITechSummaryRow): void {
    const searchParams = {
      themes: [{
        api_query: null,
        name: row.cluster_name,
        theme_search_type: 'cluster',
        cluster_id: String(row.cluster_id),
        cluster_name: row.cluster_name,
      }],
    };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'theme' }));
  }

  openCompanyThemePopup(row: ITechSummaryRow): void {
    const entityid = this.entityids?.[0];
    if (!entityid) return;
    this.dialog.open(CompanyThemePopupComponent, {
      width: '90vw',
      height: '85vh',
      maxWidth: '1200px',
      maxHeight: '800px',
      position: { top: '80px' },
      data: {
        entityid,
        entity_name: this.tab_name ?? '',
        theme_label: row.cluster_name,
        theme_search_type: 'cluster',
        cluster_id: String(row.cluster_id),
        cluster_name: row.cluster_name,
        tabId: this.tabId,
      },
    });
  }
}
